import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FaArrowLeft, FaLink, FaSpinner, FaExternalLinkAlt } from 'react-icons/fa';
import { fetchToken } from '../../../lib/globalApiClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import './findSimilar.css';

const BACKEND_API = process.env.REACT_APP_APIUrl || 'https://api.importerr.com/api/';

const AdminFindSimilar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const message = useMessage();

  const [sortBy, setSortBy] = useState('relevance');
  const [priceRange, setPriceRange] = useState('all');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [amazonProduct, setAmazonProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(false);
  const [mappingProductId, setMappingProductId] = useState(null);
  const [pricingCache, setPricingCache] = useState({});

  // Get ASIN and image from query params
  const searchParams = new URLSearchParams(location.search);
  const asinFromQuery = searchParams.get('asin') || '';
  const imageFromQuery = searchParams.get('image') || '';
  const titleFromQuery = searchParams.get('title') || '';
  const priceFromQuery = searchParams.get('price') || '';

  // Calculate INR price using pricing API
  const calculateINRPrice = useCallback(async (rmbPrice, offerId = '') => {
    if (!rmbPrice || rmbPrice <= 0) return 0;

    const cacheKey = rmbPrice.toFixed(2);
    if (pricingCache[cacheKey]) {
      return pricingCache[cacheKey];
    }

    try {
      const calculationData = {
        ap: parseFloat(rmbPrice),
        ls: 0,
        hsn: false,
        hsnTaxPercent: 18,
        dw: 0.5,
        vw: 0.5 / 5000,
        sku: String(offerId || ''),
      };

      const response = await fetch(`${BACKEND_API}product/pricing-algorithms/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(calculationData)
      });

      if (!response.ok) return 0;

      const data = await response.json();

      if (data?.data?.breakdown?.FBP) {
        const inrPrice = parseFloat(data.data.breakdown.FBP);
        setPricingCache(prev => ({ ...prev, [cacheKey]: inrPrice }));
        return inrPrice;
      }

      if (data?.breakdown?.FBP) {
        const inrPrice = parseFloat(data.breakdown.FBP);
        setPricingCache(prev => ({ ...prev, [cacheKey]: inrPrice }));
        return inrPrice;
      }

      return 0;
    } catch (error) {
      console.error('Pricing calculation failed:', error);
      return 0;
    }
  }, [pricingCache]);


  // Compress image for API
  const compressImage = useCallback(async (blob) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        const maxDim = 400;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((compressedBlob) => {
          resolve(compressedBlob || blob);
        }, 'image/jpeg', 0.7);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(blob);
      };
      img.src = url;
    });
  }, []);

  // Find similar products using image
  const findSimilarProducts = async (imageUrl, page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const proxyUrl = `${BACKEND_API.replace(/\/+$/, '')}/proxy/image?url=${encodeURIComponent(imageUrl)}`;
      const imgResponse = await fetch(proxyUrl);

      if (!imgResponse.ok) throw new Error('Failed to fetch image');

      let imgBlob = await imgResponse.blob();
      imgBlob = await compressImage(imgBlob);

      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(imgBlob);
      });

      const baseUrl = process.env.REACT_APP_APIUrl1688;
      const token = await fetchToken();
      const response = await fetch(baseUrl.replace(/\/$/, '') + '/get-product-info-by-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Token': token
        },
        body: JSON.stringify({
          imageBase64: base64Image,
          beginPage: page,
          pageSize: 20,
          country: 'en',
        }),
      });

      if (!response.ok) throw new Error('API error');

      const data = await response.json();

      let productsData = null;
      if (data.status === 'success' && data.products) {
        productsData = data.products;
      } else if (data.result?.success && data.result?.result?.data) {
        productsData = data.result.result.data;
      } else if (data.products) {
        productsData = data.products;
      } else if (data.data) {
        productsData = data.data;
      } else if (Array.isArray(data)) {
        productsData = data;
      }

      if (productsData && Array.isArray(productsData) && productsData.length > 0) {
        const productsWithPrices = await Promise.all(
          productsData.map(async (item, index) => {
            const rmbPrice = parseFloat(item.priceInfo?.price || item.price || 0);
            const offerId = item.offerId || item.id || '';
            const inrPrice = await calculateINRPrice(rmbPrice, offerId);

            const variants = [];
            const skuInfos = item.productSkuInfos || item.skuInfos || item.detailed_info?.productSkuInfos || [];
            if (skuInfos && Array.isArray(skuInfos)) {
              skuInfos.slice(0, 5).forEach((sku, idx) => {
                if (sku.skuId) {
                  variants.push({
                    skuId: sku.skuId,
                    price: sku.price || sku.consignPrice || rmbPrice,
                    attributes: sku.skuAttributes?.map(attr => attr.valueTrans || attr.value).join(', ') || `Variant ${idx + 1}`
                  });
                }
              });
            }

            return {
              id: offerId || `product-${index}`,
              offerId: offerId,
              title: item.subjectTrans || item.subject || item.title || 'Product Title',
              image: item.imageUrl || item.image || item.productImage?.images?.[0] || '',
              price: { rmb: rmbPrice, inr: inrPrice },
              similarity: Math.max(95 - index * 2, 70),
              supplier: item.detailed_info?.productShippingInfo?.sendGoodsAddressText || item.supplier || item.companyName || 'Supplier',
              monthSold: item.monthSold || 0,
              variants: variants,
            };
          })
        );

        if (data.pagination) {
          setHasMoreProducts(page < (data.pagination.totalPage || 1));
        }

        if (append) {
          setSimilarProducts(prev => [...prev, ...productsWithPrices]);
        } else {
          setSimilarProducts(productsWithPrices);
        }
      } else {
        if (!append) setSimilarProducts([]);
      }
    } catch (error) {
      console.error('Find similar failed:', error);
      message.showToast('Failed to find similar products', ToastTypes.ERROR);
      if (!append) setSimilarProducts([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };


  // Map product to ASIN
  const handleMapProduct = async (product, skuId = null) => {
    const asin = amazonProduct?.asin || asinFromQuery;
    if (!asin || !product.offerId) {
      message.showToast('Missing ASIN or Product ID', ToastTypes.ERROR);
      return;
    }

    const mapKey = skuId ? `${product.id}-${skuId}` : product.id;
    setMappingProductId(mapKey);

    try {
      const token = localStorage.getItem('LOGIN_HEADER');
      if (!token) {
        message.showToast('Please login to map products', ToastTypes.ERROR);
        return;
      }

      const response = await fetch(`${BACKEND_API}admin/map-asin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          asin: asin,
          offerId: product.offerId.toString(),
          skuId: skuId || null,
          productTitle: product.title,
          productImage: product.image,
          productPrice: product.price.rmb,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const mappedTo = skuId ? `SKU ${skuId}` : `product ${product.offerId}`;
        message.showToast(`Successfully mapped ASIN ${asin} to ${mappedTo}`, ToastTypes.SUCCESS);
      } else {
        message.showToast(data.message || 'Failed to map product', ToastTypes.ERROR);
      }
    } catch (error) {
      console.error('Map product failed:', error);
      message.showToast('Failed to map product. Please try again.', ToastTypes.ERROR);
    } finally {
      setMappingProductId(null);
    }
  };

  // Open product on 1688
  const handleOpenProduct = (product) => {
    if (product.offerId) {
      window.open(`https://detail.1688.com/offer/${product.offerId}.html`, '_blank');
    }
  };

  // Load more products
  const loadMoreProducts = async () => {
    if (!amazonProduct?.image || loadingMore || !hasMoreProducts) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await findSimilarProducts(amazonProduct.image, nextPage, true);
  };

  // Initialize page
  useEffect(() => {
    const initializePage = async () => {
      let product = null;

      if (imageFromQuery || asinFromQuery) {
        product = {
          asin: asinFromQuery,
          image: imageFromQuery ? decodeURIComponent(imageFromQuery) : null,
          title: titleFromQuery ? decodeURIComponent(titleFromQuery) : `Amazon Product - ${asinFromQuery}`,
          price: priceFromQuery ? decodeURIComponent(priceFromQuery) : '',
        };
      }

      if (product) {
        setAmazonProduct(product);
        setCurrentPage(1);
        setHasMoreProducts(false);

        if (product.image) {
          await findSimilarProducts(product.image, 1, false);
        }
      }
    };

    initializePage();
  }, [imageFromQuery, asinFromQuery]);

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...similarProducts];

    if (priceRange !== 'all') {
      const [min, max] = priceRange === '200+' ? [200, Infinity] : priceRange.split('-').map(Number);
      filtered = filtered.filter(product => {
        const price = product.price.rmb;
        return price >= min && (max === Infinity || price <= max);
      });
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low': return a.price.rmb - b.price.rmb;
        case 'price-high': return b.price.rmb - a.price.rmb;
        case 'similarity': return b.similarity - a.similarity;
        default: return 0;
      }
    });

    return filtered;
  }, [similarProducts, sortBy, priceRange]);

  const handleImageError = (e) => {
    e.target.style.display = 'none';
    e.target.parentElement.innerHTML = '<div class="afs-no-image"><span>No Image</span></div>';
  };

  if (!amazonProduct) {
    return (
      <div className="afs-container">
        <div className="afs-loading">
          <FaSpinner className="afs-spinner" />
          <p>Loading product details...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="afs-container">
      {/* Header */}
      <div className="afs-header">
        <button className="afs-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <h2 className="afs-title">Find Similar Products & Map to ASIN</h2>
      </div>

      {/* Amazon Product Reference */}
      <div className="afs-amazon-card">
        <div className="afs-amazon-badge">Amazon Product</div>
        <div className="afs-amazon-content">
          {amazonProduct.image && (
            <img
              src={amazonProduct.image}
              alt={amazonProduct.title}
              className="afs-amazon-img"
              crossOrigin="anonymous"
              onError={handleImageError}
            />
          )}
          <div className="afs-amazon-info">
            <h3 className="afs-amazon-title">{amazonProduct.title}</h3>
            <div className="afs-amazon-meta">
              <span className="afs-asin">ASIN: {amazonProduct.asin || asinFromQuery}</span>
              {amazonProduct.price && <span className="afs-amazon-price">{amazonProduct.price}</span>}
            </div>
            <p className="afs-hint">
              <FaLink /> Select a product below to map it to this ASIN
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="afs-filters">
        <div className="afs-filter-group">
          <label>SORT BY:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="relevance">Relevance</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="similarity">Similarity</option>
          </select>
        </div>
        <div className="afs-filter-group">
          <label>PRICE RANGE:</label>
          <select value={priceRange} onChange={(e) => setPriceRange(e.target.value)}>
            <option value="all">All Prices</option>
            <option value="0-50">¥0 - ¥50</option>
            <option value="50-100">¥50 - ¥100</option>
            <option value="100-200">¥100 - ¥200</option>
            <option value="200+">¥200+</option>
          </select>
        </div>
      </div>

      {/* Results */}
      <div className="afs-results">
        <div className="afs-results-header">
          <span>{filteredAndSortedProducts.length} similar products found</span>
          {loading && <span className="afs-searching"> - Searching...</span>}
        </div>

        {loading && similarProducts.length === 0 && (
          <div className="afs-loading">
            <FaSpinner className="afs-spinner" />
            <p>Searching for similar products on BTPL/Global...</p>
          </div>
        )}

        <div className="afs-grid">
          {filteredAndSortedProducts.map(product => (
            <div key={product.id} className="afs-card">
              <div className="afs-match-badge">{product.similarity}% match</div>

              <div className="afs-card-image" onClick={() => handleOpenProduct(product)} title="Click to open on 1688">
                <img
                  src={product.image}
                  alt={product.title}
                  referrerPolicy="no-referrer"
                  crossOrigin="anonymous"
                  onError={handleImageError}
                />
              </div>

              <div className="afs-card-body">
                <h4 className="afs-card-title" title={product.title} onClick={() => handleOpenProduct(product)}>
                  {product.title}
                </h4>
                <p className="afs-card-supplier">{product.supplier}</p>
                <p className="afs-card-offerid">Offer ID: {product.offerId}</p>

                <div className="afs-card-prices">
                  <div className="afs-price-row">
                    <span className="afs-price-label">RMB:</span>
                    <span className="afs-price-rmb">¥{product.price.rmb.toFixed(2)}</span>
                  </div>
                  <div className="afs-price-row">
                    <span className="afs-price-label">INR (Algo):</span>
                    <span className="afs-price-inr">₹{product.price.inr.toFixed(2)}</span>
                  </div>
                </div>

                {/* Variants Section */}
                {product.variants && product.variants.length > 0 && (
                  <div className="afs-variants-section">
                    <div className="afs-variants-title">Variants ({product.variants.length})</div>
                    <div className="afs-variants-list">
                      {product.variants.map((variant, idx) => (
                        <div key={variant.skuId || idx} className="afs-variant-item">
                          <span className="afs-variant-sku" title={variant.attributes}>
                            {variant.skuId} - {variant.attributes}
                          </span>
                          <button
                            className="afs-variant-map-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMapProduct(product, variant.skuId);
                            }}
                            disabled={mappingProductId === `${product.id}-${variant.skuId}`}
                          >
                            {mappingProductId === `${product.id}-${variant.skuId}` ? '...' : 'Map'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="afs-card-actions">
                  <button className="afs-btn-map" onClick={() => handleMapProduct(product)} disabled={mappingProductId === product.id}>
                    {mappingProductId === product.id ? 'Mapping...' : 'Map to ASIN'}
                  </button>
                  <button className="afs-btn-view" onClick={() => handleOpenProduct(product)}>
                    <FaExternalLinkAlt /> View on 1688
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {!loading && filteredAndSortedProducts.length === 0 && (
          <div className="afs-no-results">
            <p>No similar products found. Try a different image or search manually.</p>
          </div>
        )}
      </div>

      {hasMoreProducts && (
        <div className="afs-load-more">
          <button className="afs-btn-load-more" onClick={loadMoreProducts} disabled={loading || loadingMore}>
            {loadingMore ? 'Loading More...' : 'Load More Products'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminFindSimilar;
